import { useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { AgentComment } from "@/react/components/AgentProfileModal/Body/AgentComment";
import { chatBotPath } from "@/react/helpers/urlHelpers";
import { useBotCommentsInfinite } from "@/react/hooks/agent_profile/useBotCommentsInfinite";
import EmptyState from "@circle-react/components/ProfileModalV3/Body/EmptyState";
import { TabItem } from "@circle-react/components/ProfileModalV3/Body/TabItem";
import { SuggestedQuestionButton } from "@circle-react/components/SettingsApp/CommunityInbox/BotChatRoom/Shared/SuggestedQuestionButton";
import { useCreateBotChat } from "@circle-react/components/SettingsApp/CommunityInbox/hooks/useCreateBotChat";
import { LoaderRow } from "@circle-react/components/shared/LoaderRow";
import { textToRichText } from "@circle-react/helpers/richTextHelpers";
import type { Profile } from "@circle-react/types/CommunityBot/Profile";
import type { Chat } from "@circle-react/types/CommunityInbox/Chat";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { useCommunityAiAgentProfileContext } from "../context";

const AGENT_PROFILE_SCROLLABLE_TARGET_ID = "agent-profile-scrollable-target";

export const Body = () => {
  const { agent, onClose } = useCommunityAiAgentProfileContext();
  const { can_interact: canInteract } = agent;
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<"about" | "comments">("about");

  const profileId = agent?.profile?.id;
  const {
    fetchNextPage,
    hasNextPage,
    isLoading: isCommentsLoading,
    error: commentsError,
    comments,
  } = useBotCommentsInfinite({ profileId, enabled: activeTab === "comments" });
  // Prefer the scoped comments_count from the agent serializer
  // (which accounts for the viewer's visibility) and fall back
  // to the profile count if not present.
  const commentsCount =
    agent?.comments_count ?? agent?.profile?.comments_count ?? 0;
  const memberForComment = useMemo(
    () => ({
      id: agent?.id ?? 0,
      name: agent?.name ?? "",
      avatar_url: agent?.avatar_url ?? "",
    }),
    [agent?.id, agent?.name, agent?.avatar_url],
  );

  const { createChat } = useCreateBotChat();
  const askAgent = (profile: Profile, question: string) => {
    void createChat({
      id: profile.id,
      body: textToRichText(question),
    })
      .then((chat: Chat) => {
        history.push(chatBotPath({ id: chat.id.toString() }));
      })
      .catch(e => {
        console.error(e);
      });
  };

  return (
    <div className="bg-primary col-span-12 flex flex-col overflow-auto overflow-x-hidden p-8 md:col-span-8">
      {/* Tabs */}
      <div className="pb-5 pt-2">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <TabItem
            title={t("about")}
            isActive={activeTab === "about"}
            onClick={() => setActiveTab("about")}
          />
          <TabItem
            title={t("comments.comments")}
            isActive={activeTab === "comments"}
            count={commentsCount}
            onClick={() => setActiveTab("comments")}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div
        className="flex-1 overflow-auto"
        id={AGENT_PROFILE_SCROLLABLE_TARGET_ID}
      >
        {activeTab === "about" ? (
          <div className="pb-8">
            <div className="flex flex-col items-start gap-6">
              <div className="flex flex-col gap-3">
                <Typography.LabelSm weight="semibold" color="text-dark">
                  {t("community_bot.profiles.bio_label")}
                </Typography.LabelSm>
                <Typography.BodySm color="text-default">
                  {agent.bio}
                </Typography.BodySm>
              </div>
              <div className="flex flex-col gap-3">
                {canInteract &&
                  agent.suggested_questions &&
                  agent.suggested_questions.length > 0 && (
                    <>
                      <Typography.LabelSm weight="semibold" color="text-dark">
                        {t("community_bot.profiles.suggested_questions_label")}
                      </Typography.LabelSm>
                      <div className="flex flex-wrap gap-2">
                        {agent.suggested_questions.map((question: string) => (
                          <SuggestedQuestionButton
                            key={question}
                            question={question}
                            onClick={() => askAgent(agent.profile, question)}
                          />
                        ))}
                      </div>
                    </>
                  )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            {isCommentsLoading ? (
              <LoaderRow />
            ) : commentsError ? (
              <div className="flex h-32 flex-col items-center justify-center px-2 text-center">
                <p className="text-sm text-[#dc2626]">
                  {t("community_inbox.profile.error_loading_comments")}
                </p>
              </div>
            ) : comments.length > 0 ? (
              <div>
                <InfiniteScroll
                  style={{ overflow: "initial" }}
                  scrollThreshold={0.5}
                  next={fetchNextPage}
                  hasMore={!!hasNextPage}
                  loader={<LoaderRow />}
                  dataLength={comments.length}
                  scrollableTarget={AGENT_PROFILE_SCROLLABLE_TARGET_ID}
                  className="divide-primary space-y-2 divide-y"
                >
                  {comments.map((comment: any) => (
                    <AgentComment
                      key={comment.id}
                      comment={comment}
                      member={memberForComment}
                      onClose={() => onClose?.()}
                    />
                  ))}
                </InfiniteScroll>
              </div>
            ) : (
              <div>
                <EmptyState
                  title={t("profile_drawer.comments.empty_state.title")}
                  subtitle={t("profile_drawer.comments.empty_state.subtitle")}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
